import type { CardCategoryDocument, CardTemplateDocument, StructuredCardDocument } from "./product";
import type { ContextVisibility, JsonObject, WorldDocument } from "./types";

export interface WorldContextRequest {
  activeDocumentIds: string[];
  retrievedDocumentIds?: string[];
  maxCharacters?: number;
  fieldAllowlist?: Record<string, string[]>;
}

export interface WorldContextFragment {
  documentId: string;
  kind: WorldDocument["kind"];
  priority: "pinned" | "active" | "retrieved";
  content: string;
}

export interface WorldRetrievalRequest {
  query: string;
  activeDocumentIds?: string[];
  semanticScores?: Record<string, number>;
  limit?: number;
}

export interface WorldRetrievalHit {
  documentId: string;
  score: number;
  reasons: ("name" | "alias" | "tag" | "summary" | "reference" | "semantic")[];
}

export interface LibraryContextEntry {
  id: string;
  kind: string;
  title: string;
  content: string;
  summary?: string;
  triggers?: string[];
  tags?: string[];
  priority?: number;
  pinned?: boolean;
  visibility?: ContextVisibility;
  enabled?: boolean;
  updatedAt?: number;
}

export interface LibraryContextRequest {
  query: string;
  activeEntryIds?: string[];
  retrievedEntryIds?: string[];
  maxCharacters?: number;
  maxEntries?: number;
  maxPerKind?: number;
}

export interface LibraryContextFragment {
  entryId: string;
  kind: string;
  priority: "pinned" | "active" | "retrieved" | "matched" | "partial";
  mode: "full" | "summary";
  score: number;
  content: string;
}

export type ContextSectionStability = "platform_static" | "experience_static" | "session_dynamic" | "turn_dynamic";
export type ContextSectionMode = "full" | "clipped" | "omitted";

export interface ContextSectionInput {
  id: string;
  label: string;
  stability: ContextSectionStability;
  content: string;
  sourceIds?: string[];
  required?: boolean;
  priority?: number;
  maxCharacters?: number;
}

export interface ContextSectionResult extends ContextSectionInput {
  content: string;
  originalCharacters: number;
  includedCharacters: number;
  mode: ContextSectionMode;
  reason?: string;
}

export interface ContextPacket {
  maxCharacters: number;
  usedCharacters: number;
  sections: ContextSectionResult[];
  sourceIds: string[];
}

function selectFields(document: WorldDocument, allowlist: Record<string, string[]>): JsonObject {
  const keys = allowlist[document.kind] ?? [];
  if (!keys.length) return document.fields;
  return Object.fromEntries(keys.filter((key) => key in document.fields).map((key) => [key, document.fields[key]!])) as JsonObject;
}

export function planWorldContext(documents: readonly WorldDocument[], request: WorldContextRequest): WorldContextFragment[] {
  const activeIds = new Set(request.activeDocumentIds);
  const retrievedIds = new Set(request.retrievedDocumentIds ?? []);
  const activeReferences = new Set(documents.filter((document) => activeIds.has(document.id)).flatMap((document) => document.references.map((reference) => reference.targetId)));
  const candidates = documents.flatMap((document): WorldContextFragment[] => {
    if (document.deletedAt || document.visibility === "hidden") return [];
    let priority: WorldContextFragment["priority"] | undefined;
    if (document.visibility === "pinned") priority = "pinned";
    else if (activeIds.has(document.id)) priority = "active";
    else if (document.visibility === "partial") priority = "active";
    else if (retrievedIds.has(document.id)) priority = "retrieved";
    if (!priority) return [];
    const isCompact = priority === "retrieved" || (document.visibility === "partial" && !activeIds.has(document.id));
    const content = isCompact
      // Partial and retrieved documents stay compact (L0 directory record).
      // Agent reads full canonical fields via world.read when needed.
      ? JSON.stringify({ id: document.id, revision: document.revision, fileTypeId: document.fileTypeId, kind: document.kind, title: document.title, summary: document.summary, references: document.references })
      : JSON.stringify({ id: document.id, revision: document.revision, fileTypeId: document.fileTypeId, kind: document.kind, title: document.title, summary: document.summary, fields: selectFields(document, request.fieldAllowlist ?? {}), runtimeState: Object.fromEntries(Object.entries(document.runtimeState ?? {}).filter(([key]) => !["imageAutoRequest","imageAutoResult"].includes(key))), references: document.references });
    return [{
      documentId: document.id,
      kind: document.kind,
      priority,
      content,
    }];
  });
  const rank = { pinned: 0, active: 1, retrieved: 2 } as const;
  candidates.sort((left, right) => rank[left.priority] - rank[right.priority] || left.documentId.localeCompare(right.documentId));
  const result: WorldContextFragment[] = [];
  let used = 0;
  for (const candidate of candidates) {
    if (used + candidate.content.length > (request.maxCharacters ?? 24_000)) continue;
    result.push(candidate);
    used += candidate.content.length;
  }
  return result;
}

const searchableWords = (value: string) => [...new Set(value.toLocaleLowerCase().split(/[^\p{L}\p{N}_-]+/u).map((word) => word.trim()).filter((word) => word.length > 1))];
const includesPhrase = (source: string, phrase: string) => phrase.length > 1 && source.includes(phrase);
const clipped = (value: string, length: number) => value.length <= length ? value : `${value.slice(0, Math.max(0, length - 1)).trimEnd()}…`;

/**
 * Hybrid retrieval with inspectable reasons. Vector/search infrastructure can
 * provide semantic scores, while aliases, tags and graph references remain a
 * deterministic fallback for local and offline sessions.
 */
export function retrieveWorldDocuments(documents: readonly WorldDocument[], request: WorldRetrievalRequest): WorldRetrievalHit[] {
  const query = request.query.toLocaleLowerCase().trim();
  const words = searchableWords(query);
  const activeIds = new Set(request.activeDocumentIds ?? []);
  const activeReferences = new Set(documents.filter((document) => activeIds.has(document.id)).flatMap((document) => document.references.map((reference) => reference.targetId)));
  const semanticScores = request.semanticScores ?? {};
  return documents.flatMap((document): WorldRetrievalHit[] => {
    if (document.deletedAt || document.visibility === "hidden") return [];
    const reasons = new Set<WorldRetrievalHit["reasons"][number]>();
    let score = 0;
    const title = document.title.toLocaleLowerCase();
    const aliases = (document.aliases ?? []).map((value) => value.toLocaleLowerCase());
    const tags = (document.tags ?? []).map((value) => value.toLocaleLowerCase());
    const summary = document.summary.toLocaleLowerCase();
    if (includesPhrase(query, title) || words.some((word) => includesPhrase(title, word))) { reasons.add("name"); score += 9_000; }
    if (aliases.some((value) => includesPhrase(query, value) || includesPhrase(value, query) || words.some((word) => includesPhrase(value, word)))) { reasons.add("alias"); score += 7_000; }
    const tagHits = tags.filter((value) => includesPhrase(query, value) || includesPhrase(value, query) || words.some((word) => includesPhrase(value, word))).length;
    if (tagHits) { reasons.add("tag"); score += tagHits * 5_000; }
    const summaryHits = words.filter((word) => includesPhrase(summary, word)).length;
    if (summaryHits || includesPhrase(summary, query)) { reasons.add("summary"); score += Math.max(1, summaryHits) * 1_000; }
    if (activeReferences.has(document.id)) { reasons.add("reference"); score += 8_000; }
    const semantic = Number(semanticScores[document.id] ?? 0);
    if (semantic > 0) { reasons.add("semantic"); score += Math.min(1, semantic) * 10_000; }
    if (!score) return [];
    return [{ documentId: document.id, score, reasons: [...reasons] }];
  }).sort((left, right) => right.score - left.score || left.documentId.localeCompare(right.documentId)).slice(0, Math.max(0, request.limit ?? 12));
}

/**
 * Produces an explainable, bounded context packet. Required sections reserve
 * room first; optional sections then compete by priority. Every omitted or
 * clipped section remains in diagnostics so Control Room can explain why.
 */
export function assembleContextPacket(inputs: readonly ContextSectionInput[], maxCharacters: number): ContextPacket {
  const budget = Math.max(0, Math.trunc(maxCharacters));
  const normalized = inputs.map((input, index) => ({
    ...input,
    sourceIds: [...new Set(input.sourceIds ?? [])],
    required: input.required ?? false,
    priority: Number.isFinite(input.priority) ? Number(input.priority) : 50,
    maxCharacters: input.maxCharacters == null ? undefined : Math.max(0, Math.trunc(input.maxCharacters)),
    index,
  }));
  const order = [...normalized].sort((left, right) => Number(right.required) - Number(left.required) || right.priority - left.priority || left.index - right.index);
  const selected = new Map<string, ContextSectionResult>();
  let usedCharacters = 0;

  for (const input of order) {
    const originalCharacters = input.content.length;
    const allowedBySection = input.maxCharacters == null ? originalCharacters : Math.min(originalCharacters, input.maxCharacters);
    const remaining = Math.max(0, budget - usedCharacters);
    const allowed = Math.min(allowedBySection, remaining);
    let content = "";
    let mode: ContextSectionMode = "omitted";
    let reason: string | undefined;
    if (allowed > 0) {
      content = clipped(input.content, allowed);
      mode = content.length < originalCharacters ? "clipped" : "full";
      if (mode === "clipped") reason = allowedBySection < originalCharacters ? "section_limit" : "packet_budget";
      usedCharacters += content.length;
    } else reason = remaining <= 0 ? "packet_budget" : "insufficient_space";
    selected.set(input.id, {
      id: input.id,
      label: input.label,
      stability: input.stability,
      content,
      sourceIds: input.sourceIds,
      required: input.required,
      priority: input.priority,
      ...(input.maxCharacters == null ? {} : { maxCharacters: input.maxCharacters }),
      originalCharacters,
      includedCharacters: content.length,
      mode,
      ...(reason ? { reason } : {}),
    });
  }

  const sections = normalized.map((input) => selected.get(input.id)!);
  return {
    maxCharacters: budget,
    usedCharacters,
    sections,
    sourceIds: [...new Set(sections.filter((section) => section.mode !== "omitted").flatMap((section) => section.sourceIds ?? []))],
  };
}

/**
 * Ranks a candidate window from a large library, then emits only a strictly
 * bounded prompt fragment. Production search may supply `retrievedEntryIds`
 * from a database/vector index; local stories can use trigger/title matching.
 */
export function planLibraryContext(entries: readonly LibraryContextEntry[], request: LibraryContextRequest): LibraryContextFragment[] {
  const query = request.query.toLocaleLowerCase().trim();
  const queryWords = searchableWords(query);
  const activeIds = new Set(request.activeEntryIds ?? []);
  const retrievedIds = new Set(request.retrievedEntryIds ?? []);
  const budget = Math.max(0, request.maxCharacters ?? 12_000);
  const maxEntries = Math.max(0, request.maxEntries ?? 10);
  const maxPerKind = Math.max(1, request.maxPerKind ?? 4);

  const ranked = entries.flatMap((entry) => {
    if (entry.enabled === false) return [];
    // Explicit visibility is authoritative. A legacy `pinned` flag may only
    // supply the default when no modern context policy has been assigned.
    const visibility: ContextVisibility = entry.visibility ?? (entry.pinned ? "pinned" : "searchable");
    if (visibility === "hidden") return [];
    const title = entry.title.toLocaleLowerCase();
    const triggers = (entry.triggers ?? []).map((item) => item.toLocaleLowerCase().trim()).filter(Boolean);
    const tags = (entry.tags ?? []).map((item) => item.toLocaleLowerCase().trim()).filter(Boolean);
    const active = activeIds.has(entry.id);
    const retrieved = retrievedIds.has(entry.id);
    const exactTrigger = triggers.some((trigger) => includesPhrase(query, trigger));
    const titleMatch = includesPhrase(query, title);
    const tokenHits = queryWords.filter((word) => includesPhrase(title, word) || triggers.some((trigger) => includesPhrase(trigger, word)) || tags.some((tag) => includesPhrase(tag, word))).length;
    const partialDirectory = visibility === "partial" && !active && !retrieved && !exactTrigger && !titleMatch && tokenHits === 0;
    if (visibility !== "pinned" && !partialDirectory && !active && !retrieved && !exactTrigger && !titleMatch && tokenHits === 0) return [];
    const score = (visibility === "pinned" ? 1_000_000 : 0) + (active ? 900_000 : 0) + (retrieved ? 100_000 : 0) +
      (exactTrigger ? 20_000 : 0) + (titleMatch ? 10_000 : 0) + tokenHits * 1_000 + Math.max(0, Math.min(100, entry.priority ?? 50));
    const priority: LibraryContextFragment["priority"] = visibility === "pinned" ? "pinned" : active ? "active" : retrieved ? "retrieved" : partialDirectory ? "partial" : "matched";
    return [{ entry, score, priority, forceSummary: partialDirectory }];
  }).sort((left, right) => right.score - left.score || (right.entry.updatedAt ?? 0) - (left.entry.updatedAt ?? 0) || left.entry.id.localeCompare(right.entry.id));

  const result: LibraryContextFragment[] = [];
  const kinds = new Map<string, number>();
  let used = 0;
  for (const candidate of ranked) {
    if (result.length >= maxEntries || used >= budget) break;
    const kindCount = kinds.get(candidate.entry.kind) ?? 0;
    if (kindCount >= maxPerKind && candidate.priority !== "pinned" && candidate.priority !== "active") continue;
    const header = `[${candidate.entry.kind}] ${candidate.entry.title}\n`;
    const full = `${header}${candidate.entry.content}`;
    const summary = `${header}${candidate.entry.summary?.trim() || clipped(candidate.entry.content, 480)}`;
    const remaining = budget - used;
    if (remaining <= header.length + 8) break;
    const wantsFull = !candidate.forceSummary && (candidate.priority === "pinned" || candidate.priority === "active" || candidate.priority === "retrieved" || candidate.score >= 20_000);
    const selected = wantsFull && full.length <= remaining ? full : summary.length <= remaining ? summary : clipped(summary, remaining);
    if (!selected.trim()) continue;
    result.push({ entryId: candidate.entry.id, kind: candidate.entry.kind, priority: candidate.priority, mode: selected === full ? "full" : "summary", score: candidate.score, content: selected });
    used += selected.length;
    kinds.set(candidate.entry.kind, kindCount + 1);
  }
  return result;
}

const fileSlug = (value: string) => value.trim().toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-|-$/g, "") || "file";

/** A compact, cache-friendly catalogue. Heavy card values are intentionally omitted. */
export function buildCreatorFilesystemManifest(
  categories: readonly CardCategoryDocument[],
  templates: readonly CardTemplateDocument[],
  cards: readonly StructuredCardDocument[],
  maxCharacters = 8_000,
): string {
  const lines = [
    "<filesystem>",
    "Creator-defined file types. Read only matched or active file contents; Partial entries below provide directory metadata only.",
  ];
  for (const category of [...categories].filter((item) => item.enabled).sort((a, b) => a.order - b.order).slice(0, 64)) {
    const template = templates.find((item) => item.id === category.templateId || item.categoryId === category.id);
    if (!template?.enabled || template.contextVisibility === "hidden") continue;
    const count = cards.filter((card) => card.categoryId === category.id && card.enabled).length;
    const fields = template.fields.slice(0, 32).map((field) => `${field.key}:${field.type}${field.required ? "!" : ""}`).join(", ");
    lines.push(`/${category.name}/ — type=${category.id}; template=${template.id}@${template.version}; designation=${template.designation || fileSlug(category.name)}; .${fileSlug(category.name)}.${template.contentType}; ${count} files; ${template.contextVisibility}; fields [${fields}]`);
  }
  lines.push("</filesystem>");
  const content = lines.join("\n");
  return content.length <= maxCharacters ? content : `${content.slice(0, Math.max(0, maxCharacters - 2)).trimEnd()}…\n`;
}
